// beltData.ts - the main asteroid belt and the Kuiper belt, as data.
//
// Two decorative particle fields, not a simulation: nobody here is tracking a
// hundred thousand real asteroids, so every particle is a single draw from
// its belt's own published radial, inclination and eccentricity distribution
// (belts.ts turns a draw into a heliocentric position). The numbers below are
// the only place those distributions live - nothing is hard-coded in the
// draw call.
//
// Sources
// -------
// Main belt: its densest region sits between the 4:1 and 2:1 Kirkwood gaps,
// 2.06 and 3.27 AU, and the population's orbital distribution peaks near an
// inclination under 4 deg and an eccentricity near 0.07, with almost every
// body under i = 30 deg (Wikipedia, "Asteroid belt", retrieved 2026-09-12,
// https://en.wikipedia.org/wiki/Asteroid_belt; cross-checked against NASA's
// own summary at https://science.nasa.gov/solar-system/asteroids/facts/,
// same date). Two more of the belt's own Kirkwood gaps are carved into the
// radial profile below as density notches: the 3:1 at 2.50 AU and the 5:2 at
// 2.82 AU.
//
// Kuiper belt: NASA gives the belt's main region as 30 to 50 AU
// (https://science.nasa.gov/solar-system/kuiper-belt/facts/, retrieved
// 2026-09-12). Wikipedia's "Kuiper belt" (same retrieval) places the
// non-resonant "classical" population between Neptune's 2:3 and 1:2
// mean-motion resonances, 42-48 AU, and splits it into a dynamically cold
// group under about i = 10 deg and a dynamically hot group out past i = 30
// deg. This field draws one blended inclination distribution across the
// whole belt rather than the two populations separately - a simplification
// `belts.ts` repeats in its own comment.

export interface BeltDef {
  readonly id: string;
  readonly label: string;
  /** Semi-major axis is drawn from `[innerAu - padAu, outerAu + padAu]`; the
   *  pad is what lets the density profile fade rather than snap at the wall. */
  readonly innerAu: number;
  readonly outerAu: number;
  readonly padAu: number;
  /** Relative weight at that semi-major axis, rejection-sampled against
   *  `densityPeak`, which must be >= this function's true maximum. */
  readonly densityAt: (aAu: number) => number;
  readonly densityPeak: number;
  /** Half-normal, degrees: mode 0, resampled past the cap rather than clipped
   *  onto it, so the shape below the cap stays the distribution's own. */
  readonly inclinationSigmaDeg: number;
  readonly inclinationCapDeg: number;
  /** Rayleigh, mode = sigma, resampled past the cap the same way. */
  readonly eccentricitySigma: number;
  readonly eccentricityCap: number;
  readonly count: number;
  readonly colorCss: string;
  readonly pointSizePx: number;
}

function clamp01(x: number): number {
  return Math.min(1, Math.max(0, x));
}

function smoothstep(t: number): number {
  const c = clamp01(t);
  return c * c * (3 - 2 * c);
}

/** 1 inside `[inner, outer]`, easing to 0 over `margin` AU on both edges. */
function radialWindow(aAu: number, inner: number, outer: number, margin: number): number {
  return smoothstep((aAu - inner) / margin) * smoothstep((outer - aAu) / margin);
}

function gaussianDip(aAu: number, centreAu: number, sigmaAu: number, depth: number): number {
  return depth * Math.exp(-((aAu - centreAu) ** 2) / (2 * sigmaAu * sigmaAu));
}

/**
 * Instances per belt, capped well under the ORBIT band's 120-draw-call
 * budget (docs/ATLAS-TERRAIN.md, `07-performance-and-instruments.md`): each
 * belt is ONE draw call whatever its count, so the cap here is a CPU-build
 * and GPU-fill budget instead, sized so both belts build inside a frame at
 * boot the way the orrery's own warm-up does.
 */
export const BELT_INSTANCE_CAP = 12000;

export const BELTS: readonly BeltDef[] = [
  {
    id: 'main-belt',
    label: 'Main Asteroid Belt',
    innerAu: 2.06,
    outerAu: 3.27,
    padAu: 0.12,
    densityAt: (aAu) => {
      const edge = radialWindow(aAu, 2.06, 3.27, 0.10);
      const gap31 = gaussianDip(aAu, 2.50, 0.03, 0.75);
      const gap52 = gaussianDip(aAu, 2.82, 0.035, 0.6);
      return Math.max(0, edge * (1 - gap31 - gap52));
    },
    densityPeak: 1,
    inclinationSigmaDeg: 7,
    inclinationCapDeg: 30,
    eccentricitySigma: 0.07,
    eccentricityCap: 0.35,
    count: 7000,
    colorCss: '#a89a86',
    pointSizePx: 1.6,
  },
  {
    id: 'kuiper-belt',
    label: 'Kuiper Belt',
    innerAu: 30,
    outerAu: 50,
    padAu: 1.5,
    densityAt: (aAu) => {
      const edge = radialWindow(aAu, 30, 50, 1.5);
      const classical = 1 + 1.2 * radialWindow(aAu, 42, 48, 1.0);
      return edge * classical;
    },
    densityPeak: 2.2,
    inclinationSigmaDeg: 11,
    inclinationCapDeg: 30,
    eccentricitySigma: 0.06,
    eccentricityCap: 0.3,
    count: 5000,
    colorCss: '#7f97ab',
    pointSizePx: 1.6,
  },
];
